export type PublicConfig = {
  public_url: string;
  artwork_base_url: string;
  relays: string[];
  signer_relays: string[];
  open_badges_enabled: boolean;
  bitcoinplusplus_enabled: boolean;
  bitcoinplusplus_url?: string;
  managed_signer_url?: string;
};

export type BadgeDefinition = {
  event_id: string;
  issuer_pubkey: string;
  identifier: string;
  name: string;
  description: string;
  image_url: string;
  thumbnails: ArtworkImage[];
  created_at: string;
};

export type ArtworkImage = {
  url: string;
  width: number;
  height: number;
  media_type: string;
  sha256: string;
};

export type Artwork = {
  image: ArtworkImage;
  thumbnails: ArtworkImage[];
};

export type RelayPublication = {
  event_id: string;
  relay_url: string;
  attempted_at: string;
  accepted: boolean;
  message: string;
};

export type BadgeAward = {
  validity?: Record<string, { expires_at: string; updated_at: string }>;
  event_id: string;
  issuer_pubkey: string;
  badge_address: string;
  badge?: BadgeDefinition;
  recipients: string[];
  recipient_profiles?: Record<string, string>;
  created_at: string;
  publications: RelayPublication[];
  revocation?: BadgeRevocation;
  acceptances?: Record<string, BadgeAcceptance>;
};

export type BadgeAcceptance = {
  event_id: string;
  award_event_id: string;
  recipient_pubkey: string;
  created_at: string;
};

export type BadgeRevocation = {
  event_id: string;
  award_event_id: string;
  issuer_pubkey: string;
  reason?: string;
  created_at: string;
  publications: RelayPublication[];
};

export type PublicationResult = {
  event_id: string;
  published: boolean;
  publications: RelayPublication[];
};

export type Organization = {
  pubkey: string;
  display_name: string;
  website_url: string;
  linkedin_page_url: string;
  created_at: string;
  last_login_at: string;
};

export type PublicOrganization = Pick<Organization, 'pubkey' | 'display_name'> & {
	website_url?: string;
	linkedin_page_url?: string;
};

export type BitcoinPlusPlusMember = {
  id: string;
  name: string;
  role: string;
  nostr_pubkey?: string;
  profile_url?: string;
};

export type BitcoinPlusPlusOrganization = {
  id: string;
  name: string;
  website_url?: string;
  linkedin_url?: string;
  nostr?: string;
  role: string;
  can_manage: boolean;
  members?: BitcoinPlusPlusMember[];
  badge_grants?: BitcoinPlusPlusGrant[];
};

export type BitcoinPlusPlusGrant = {
  id: string;
  recipient_person_id: string;
  recipient_name: string;
  issuer_pubkey: string;
  badge_identifier: string;
  badge_name: string;
  badge_image_url: string;
	subject_profile_url?: string;
  recipient_pubkey?: string;
  state: 'granted' | 'ready_to_issue' | 'delivery_error';
};

export type BitcoinPlusPlusSession = {
  can_browse_ecosystem?: boolean;
  person_id: string;
  name: string;
  organizations: BitcoinPlusPlusOrganization[];
  expires_at: string;
};

export type BitcoinPlusPlusPerson = {
  id: string;
  public_id: string;
  name: string;
  avatar_url?: string;
  company?: string;
  nostr_pubkey?: string;
  profile_url: string;
  participation: BitcoinPlusPlusParticipation[];
};

export type BitcoinPlusPlusParticipation = {
  conference_tag: string;
  conference_name: string;
  role: 'speaker' | 'builder' | 'attendee';
};

export type BitcoinPlusPlusConference = {
  tag: string;
  description: string;
};

export type BitcoinPlusPlusPeopleQuery = {
  q?: string;
  conference?: string;
  participation?: '' | BitcoinPlusPlusParticipation['role'];
  nostr?: 'any' | 'verified' | 'missing';
  cursor?: string;
  limit?: number;
};

async function getJSON<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Request failed (${response.status})`);
  return response.json() as Promise<T>;
}

export const getConfig = () => getJSON<PublicConfig>('/api/config');
export const getHealth = () => getJSON<{ status: string }>('/api/health');
export const getBadges = () => getJSON<{ badges: BadgeDefinition[] }>('/api/badges');
export const getOrganizationBadges = () => getJSON<{ badges: BadgeDefinition[] }>('/api/organization/badges');
export const getOrganizationAwards = () => getJSON<{ awards: BadgeAward[] }>('/api/organization/awards');
export const getPublicAward = (eventID: string) => getJSON<{ award: BadgeAward; issuer: PublicOrganization }>(`/api/public/awards/${encodeURIComponent(eventID)}`);

export async function recordBadgeAcceptance(awardEventID: string, event: unknown): Promise<void> {
  const response = await fetch(`/api/public/awards/${encodeURIComponent(awardEventID)}/acceptance`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(event) });
  if (!response.ok) throw new Error(`Could not record badge acceptance (${response.status})`);
}
export const getPublicOrganization = (issuer: string) => getJSON<{ organization: PublicOrganization; badges: BadgeDefinition[] }>(`/api/public/organizations/${encodeURIComponent(issuer)}`);
export const getPublicBitcoinPlusPlusOrganization = (organizationID: string) => getJSON<{ organization: PublicOrganization; badges: BadgeDefinition[]; issuer_pubkeys: string[] }>(`/api/public/btcpp/organizations/${encodeURIComponent(organizationID)}/badges`);
export const searchBitcoinPlusPlusPeople = (query: BitcoinPlusPlusPeopleQuery) => {
  const values = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') values.set(key, String(value));
  }
  return getJSON<{ people: BitcoinPlusPlusPerson[]; next_cursor?: string }>(`/api/integrations/btcpp/people?${values}`);
};
export const getBitcoinPlusPlusConferences = () => getJSON<{ conferences: BitcoinPlusPlusConference[] }>('/api/integrations/btcpp/conferences');

export async function getBitcoinPlusPlusSession(): Promise<BitcoinPlusPlusSession | null> {
  const response = await fetch('/api/auth/btcpp/session', { headers: { Accept: 'application/json' } });
  if (response.status === 401 || response.status === 404) return null;
  if (!response.ok) throw new Error(`Could not load Bitcoin++ session (${response.status})`);
  return ((await response.json()) as { session: BitcoinPlusPlusSession }).session;
}

export async function deleteBitcoinPlusPlusSession(): Promise<void> {
  const response = await fetch('/api/auth/btcpp/session', { method: 'DELETE' });
  if (!response.ok) throw new Error(`Could not sign out of Bitcoin++ (${response.status})`);
}

export async function linkBitcoinPlusPlusOrganization(organizationID: string): Promise<{ organization: Organization; directory: BitcoinPlusPlusOrganization }> {
  const response = await fetch(`/api/integrations/btcpp/organizations/${encodeURIComponent(organizationID)}/link`, { method: 'POST', headers: { Accept: 'application/json' } });
  const result = (await response.json()) as { organization?: Organization; directory?: BitcoinPlusPlusOrganization; error?: string };
  if (!response.ok || !result.organization || !result.directory) throw new Error(result.error || `Could not link organization (${response.status})`);
  return { organization: result.organization, directory: result.directory };
}

export async function updateOrganizationProfile(displayName: string, websiteURL: string, linkedInPageURL: string): Promise<Organization> {
  const response = await fetch('/api/organization', {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ display_name: displayName, website_url: websiteURL, linkedin_page_url: linkedInPageURL })
  });
  const result = (await response.json()) as { organization?: Organization; error?: string };
  if (!response.ok || !result.organization) throw new Error(result.error || `Could not update organization (${response.status})`);
  return result.organization;
}

export async function getSession(): Promise<Organization | null> {
  const response = await fetch('/api/auth/session', { headers: { Accept: 'application/json' } });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`Could not load session (${response.status})`);
  const result = (await response.json()) as { organization: Organization };
  return result.organization;
}

export async function createSession(authorization: string, signal?: AbortSignal): Promise<Organization> {
  const response = await fetch('/api/auth/session', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: authorization },
    signal
  });
  const result = (await response.json()) as { organization?: Organization; error?: string };
  if (!response.ok || !result.organization) {
    throw new Error(result.error || `Could not sign in (${response.status})`);
  }
  return result.organization;
}

export async function deleteSession(): Promise<void> {
  const response = await fetch('/api/auth/session', { method: 'DELETE' });
  if (!response.ok) throw new Error(`Could not sign out (${response.status})`);
}

export async function uploadArtwork(blob: Blob): Promise<Artwork> {
  const body = new FormData();
  body.append('artwork', blob, 'badge.png');
  const response = await fetch('/api/organization/artwork', { method: 'POST', body });
  const result = (await response.json()) as Artwork & { error?: string };
  if (!response.ok) throw new Error(result.error || `Could not upload artwork (${response.status})`);
  return result;
}

export async function submitOrganizationEvent(event: unknown, recipientProfiles: Record<string, string> = {}, recipientPersonIDs: Record<string, string> = {}, bitcoinPlusPlusOrganizationID = '', recipientGrantIDs: Record<string, string> = {}, idempotencyKey = ''): Promise<PublicationResult> {
	const hasMetadata = Object.keys(recipientProfiles).length > 0 || Object.keys(recipientPersonIDs).length > 0 || Object.keys(recipientGrantIDs).length > 0 || idempotencyKey !== '';
	const body = hasMetadata ? { event, recipient_profiles: recipientProfiles, recipient_person_ids: recipientPersonIDs, recipient_grant_ids: recipientGrantIDs, btcpp_organization_id: bitcoinPlusPlusOrganizationID, idempotency_key: idempotencyKey } : event;
  const response = await fetch('/api/organization/events', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const result = (await response.json()) as PublicationResult & { error?: string };
  if (!response.ok) throw new Error(result.error || `Could not publish event (${response.status})`);
  return result;
}

export async function retryOrganizationEvent(eventID: string): Promise<PublicationResult> {
  const response = await fetch(`/api/organization/events/${encodeURIComponent(eventID)}/publish`, { method: 'POST' });
  const result = (await response.json()) as PublicationResult & { error?: string };
  if (!response.ok) throw new Error(result.error || `Could not retry publication (${response.status})`);
  return result;
}

export function credentialStatus(award: BadgeAward, recipient: string, now = Date.now()): 'active' | 'expired' | 'revoked' {
  if (award.revocation) return 'revoked';
  const expiry = award.validity?.[recipient]?.expires_at;
  return expiry && new Date(expiry).getTime() <= now ? 'expired' : 'active';
}

export async function renewCredential(eventID: string, recipient: string, expiresAt: string, updateID: string) {
  const response = await fetch(`/api/organization/awards/${eventID}/credentials/${recipient}/renew`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ update_id: updateID, expires_at: expiresAt })
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Could not extend credential expiry.');
}
